<script setup lang="ts">
/**
 * SubsystemPanel — T3 cruiser 4-slot subsystem display
 * Shows Core, Defensive, Offensive, Propulsion subsystem slots
 * Each can be clicked to select a subsystem via the market browser
 */
import type { FitSubsystem, SubsystemSlot } from '../../../shared/types';
import { iconUrl } from '../../composables/useApi';

const props = defineProps<{
  subsystems: FitSubsystem[];
}>();

const emit = defineEmits<{
  'select-subsystem': [slot: SubsystemSlot];
  'remove-subsystem': [id: number];
}>();

interface SubSlotDef {
  slot: SubsystemSlot;
  label: string;
}

const SUBSYSTEM_SLOTS: SubSlotDef[] = [
  { slot: 'core', label: 'Core' },
  { slot: 'defensive', label: 'Defensive' },
  { slot: 'offensive', label: 'Offensive' },
  { slot: 'propulsion', label: 'Propulsion' },
];

function getSubsystem(slot: SubsystemSlot): FitSubsystem | undefined {
  return props.subsystems.find(s => s.slot === slot);
}
</script>

<template>
  <div class="subsystem-panel">
    <div class="subsystem-header">
      <span class="subsystem-title">Subsystems</span>
    </div>
    <div class="subsystem-slots">
      <div
        v-for="def in SUBSYSTEM_SLOTS"
        :key="def.slot"
        class="subsystem-slot"
        :class="{ 'subsystem-slot--filled': !!getSubsystem(def.slot) }"
        @click="emit('select-subsystem', def.slot)"
      >
        <template v-if="getSubsystem(def.slot)">
          <img
            :src="iconUrl(getSubsystem(def.slot)!.typeID, 32)"
            :alt="getSubsystem(def.slot)!.typeName"
            class="subsystem-icon"
          />
          <div class="subsystem-info">
            <span class="subsystem-name" :title="getSubsystem(def.slot)!.typeName">
              {{ getSubsystem(def.slot)!.typeName }}
            </span>
            <span class="subsystem-slot-label">{{ def.label }}</span>
          </div>
          <button
            class="subsystem-remove"
            title="Remove subsystem"
            @click.stop="emit('remove-subsystem', getSubsystem(def.slot)!.id)"
          >
            <svg width="10" height="10" viewBox="0 0 10 10">
              <line x1="2" y1="2" x2="8" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <line x1="8" y1="2" x2="2" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
        </template>
        <template v-else>
          <div class="subsystem-empty-icon" />
          <span class="subsystem-empty-label">{{ def.label }} Subsystem</span>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.subsystem-panel {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.subsystem-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  background: rgba(255, 152, 0, 0.12);
  color: var(--accent-orange);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.subsystem-slots {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.subsystem-slot {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px;
  border-radius: var(--radius-sm);
  min-height: 30px;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.subsystem-slot--filled {
  background: var(--bg-secondary);
  border-left: 2px solid var(--accent-orange);
}

.subsystem-slot:not(.subsystem-slot--filled) {
  border: 1px dashed var(--border-color);
}

.subsystem-slot:hover {
  background: var(--bg-hover);
}

.subsystem-slot:not(.subsystem-slot--filled):hover {
  border-color: var(--text-secondary);
}

.subsystem-icon {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  flex-shrink: 0;
}

.subsystem-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.subsystem-name {
  font-size: 0.78rem;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.subsystem-slot-label {
  font-size: 0.68rem;
  color: var(--text-secondary);
  line-height: 1.2;
}

.subsystem-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  opacity: 0;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.subsystem-slot--filled:hover .subsystem-remove {
  opacity: 1;
}

.subsystem-remove:hover {
  background: rgba(244, 67, 54, 0.2);
  color: var(--accent-red);
}

.subsystem-empty-icon {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.03);
  flex-shrink: 0;
}

.subsystem-empty-label {
  font-size: 0.72rem;
  color: var(--text-secondary);
  font-style: italic;
}
</style>
